import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, WASTE_TYPES, wasteLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Recycle, Scale, Users, Award, ShieldCheck, ArrowRight, Camera, Barcode, ClipboardCheck, Leaf, Package } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LineChart, Line } from "recharts";

const fmtKg = (v) => `${(Number(v) || 0).toFixed(1)} kg`;
const fmtNum = (v) => Number(v || 0).toLocaleString("it-IT");

export default function LandingPage() {
  const { user } = useAuth();
  const cfg = useConfig();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const byTypeSorted = (stats?.by_type || [])
    .map((t) => ({ ...t, label: wasteLabel(t.waste_type), color: WASTE_TYPES.find((w) => w.value === t.waste_type)?.color || "#6b7280" }))
    .sort((a, b) => b.total_kg - a.total_kg);

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 py-3.5 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Recycle className="w-5 h-5 text-primary" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm">{cfg.comune_name} · Rifiuti</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Servizio sperimentale</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-4 text-sm text-gray-600">
            <a href="#dashboard" className="px-3 py-2 hover:text-primary" data-testid="nav-dashboard">Dashboard pubblica</a>
            <a href="#come-funziona" className="px-3 py-2 hover:text-primary" data-testid="nav-how">Come funziona</a>
            <a href="#verifica" className="px-3 py-2 hover:text-primary" data-testid="nav-verify">Verifica barcode</a>
          </nav>
          <div className="ml-auto">
            {user ? (
              <Link to="/app"><Button className="rounded-full gap-2" data-testid="cta-my-panel">Vai al mio pannello <ArrowRight className="w-4 h-4" /></Button></Link>
            ) : (
              <Link to="/login"><Button className="rounded-full gap-2" data-testid="cta-login">Accedi <ArrowRight className="w-4 h-4" /></Button></Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 80% -10%, rgba(0,82,163,0.10), transparent 60%), radial-gradient(900px 400px at 10% 20%, rgba(15,81,50,0.10), transparent 55%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 pt-20 pb-14 relative">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 rounded-full mb-5">
                <ShieldCheck className="w-3.5 h-3.5" /> Portale istituzionale
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                Ogni chilo conferito<br /><span className="text-primary">diventa uno sconto</span> sulla TARI.
              </h1>
              <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
                Il {cfg.comune_name} trasforma un gesto quotidiano in un beneficio concreto. Conferisci, pesa, fotografa: il tuo contributo viene verificato dagli operatori comunali e riconosciuto sulla bolletta.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {user
                  ? <Link to="/app"><Button size="lg" className="rounded-full h-12 px-6" data-testid="hero-my-panel">Il mio pannello <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                  : <Link to="/login"><Button size="lg" className="rounded-full h-12 px-6" data-testid="hero-login">Accedi al portale <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                }
                <a href="#come-funziona"><Button size="lg" variant="outline" className="rounded-full h-12 px-6">Come funziona</Button></a>
              </div>
            </div>

            {/* Big number card */}
            <div className="lg:col-span-2">
              <Card className="border-gray-200 overflow-hidden">
                <CardContent className="p-8 bg-gradient-to-br from-primary/5 to-emerald-500/5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Totale conferito e verificato</div>
                  <div className="font-display text-6xl font-bold text-primary mt-2" data-testid="hero-total-kg">{fmtKg(stats?.total_kg)}</div>
                  <div className="text-sm text-gray-600 mt-2">su un totale di <strong>{fmtNum(stats?.total_count)}</strong> conferimenti</div>
                  <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Cittadini attivi</div>
                      <div className="font-display text-2xl font-semibold mt-1" data-testid="hero-citizens">{fmtNum(stats?.citizens)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Aziende</div>
                      <div className="font-display text-2xl font-semibold mt-1" data-testid="hero-businesses">{fmtNum(stats?.businesses)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard pubblica */}
      <section id="dashboard" className="max-w-[1400px] mx-auto px-6 py-16 border-t border-gray-200">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Dashboard pubblica</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Trasparenza in tempo reale</h2>
            <p className="text-gray-600 mt-2 max-w-2xl">Aggregati dei conferimenti approvati sul territorio comunale. Nessun dato personale, solo numeri.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Scale} label="Kg approvati" value={fmtKg(stats?.total_kg)} tone="primary" testid="pub-stat-total" />
          <StatCard icon={Award} label="Riconosciuti TARI" value={fmtKg(stats?.recognized_kg)} tone="success" testid="pub-stat-recognized" />
          <StatCard icon={Users} label="Utenze attive" value={fmtNum((stats?.citizens || 0) + (stats?.businesses || 0))} tone="info" testid="pub-stat-users" />
          <StatCard icon={Package} label="Risme distribuite" value={fmtNum(stats?.reams)} tone="warning" testid="pub-stat-reams" />
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-3">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-lg">Andamento ultimi 12 mesi</h3>
              <p className="text-sm text-muted-foreground">Kg totali conferiti e approvati per mese.</p>
              <div className="mt-4 h-72" data-testid="chart-trend">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats?.trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <Tooltip formatter={(v) => `${v} kg`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="kg" stroke="#0052A3" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-lg">Per tipologia</h3>
              <p className="text-sm text-muted-foreground">Ripartizione dei kg approvati.</p>
              <div className="mt-4 h-72" data-testid="chart-by-type">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byTypeSorted} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: "#374151" }} width={90} />
                    <Tooltip formatter={(v) => `${v.toFixed(2)} kg`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="total_kg" radius={[0, 6, 6, 0]}>
                      {byTypeSorted.map((t, i) => <Cell key={i} fill={t.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Come funziona */}
      <section id="come-funziona" className="max-w-[1400px] mx-auto px-6 py-16 border-t border-gray-200">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Come funziona</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Quattro passi, un beneficio reale</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          <Step n={1} icon={Barcode} title="Applica il barcode" text={`Il ${cfg.comune_name} ti consegna una risma di etichette barcode uniche. Attaccale su sacchi, scatoloni o pezzi singoli monolitici.`} />
          <Step n={2} icon={Scale} title="Pesa e fotografa" text="Al punto di raccolta deposita il conferimento sul piano di pesatura e scatta una foto che includa peso e barcode." />
          <Step n={3} icon={Camera} title="Carica in app" text="Accedi al portale, carica la foto e indica la tipologia di rifiuto. Il conferimento entra in coda di verifica." />
          <Step n={4} icon={ClipboardCheck} title="Verifica & TARI" text="Un operatore certifica il peso e la tipologia. I conferimenti approvati vengono riconosciuti come sconto sulla bolletta TARI." />
        </div>
      </section>

      {/* Verifica barcode */}
      <section id="verifica" className="max-w-[1400px] mx-auto px-6 py-16 border-t border-gray-200">
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-white border border-primary/20 text-primary px-3 py-1.5 rounded-full mb-4">
                <ShieldCheck className="w-3.5 h-3.5" /> Anti-falsificazione
              </div>
              <h2 className="font-display text-3xl font-bold">Verifica un codice a barre</h2>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Ogni etichetta contiene <strong>8 cifre</strong>: le prime 6 sono il seriale univoco, le ultime 2 sono un <strong>codice di controllo</strong> generato dal {cfg.comune_name} con un algoritmo segreto (HMAC-SHA256). Un codice inventato viene rifiutato automaticamente.
              </p>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Esempio: se il codice completo stampato è <span className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded">00001343-47</span>, inserendolo qui il sistema conferma la validità e l'utente a cui è associato. Il codice <span className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded">00001340-47</span> risulterà invece invalido.
              </p>
            </div>
            <VerifyBarcodeCard />
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-8">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center gap-4 justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-emerald-600" /> Servizio sperimentale del {cfg.comune_name} · versione beta
          </div>
          <div>© {new Date().getFullYear()} {cfg.comune_name}</div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    info: "bg-blue-100 text-blue-700",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 font-display text-2xl md:text-3xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function Step({ n, icon: Icon, title, text }) {
  return (
    <Card className="border-gray-200 relative overflow-hidden">
      <CardContent className="p-6">
        <div className="absolute top-3 right-4 font-display font-bold text-4xl text-primary/10">{n}</div>
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
        <div className="mt-4 font-display font-semibold">{title}</div>
        <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{text}</p>
      </CardContent>
    </Card>
  );
}

function VerifyBarcodeCard() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      // public verify: computes checksum locally is not possible (secret). Use dedicated public endpoint? no — we only reveal validity, not user.
      // Use lookup endpoint but only if logged in. For public: expose a "compute checksum" check via /api/public/verify — but we don't have it.
      // We'll just call /api/barcodes/lookup — if unauthorized (public), backend will 401. So for public: only check length + digits.
      // Simplify: public UI ONLY checks format (8 digits), server-side validation is not exposed publicly to keep the algorithm secret.
      if (!/^\d{8}$/.test(code)) {
        setResult({ ok: false, msg: "Il codice deve avere esattamente 8 cifre." });
      } else {
        setResult({ ok: null, msg: "Codice ben formato. La validità del checksum è verificabile solo dall'operatore comunale nel back office.", info: true });
      }
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-white">
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Codice completo (8 cifre)</label>
          <input
            data-testid="verify-input"
            inputMode="numeric" maxLength={8}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="00001343 47"
            className="w-full font-mono text-2xl tracking-widest text-center border-2 border-gray-200 focus:border-primary rounded-lg py-4 outline-none"
          />
          <Button type="submit" disabled={busy || code.length !== 8} className="w-full rounded-full h-11" data-testid="verify-submit">
            Verifica formato
          </Button>
        </form>
        {result && (
          <div className={`mt-4 p-3 rounded-lg text-sm border ${
            result.info ? "bg-blue-50 border-blue-200 text-blue-900" :
            result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
            "bg-red-50 border-red-200 text-red-900"
          }`} data-testid="verify-result">
            {result.msg}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-muted-foreground">
          Verifica completa (checksum + assegnazione utente) disponibile per operatori autenticati nel back office.
        </div>
      </CardContent>
    </Card>
  );
}
